/**
 * Deterministic source-id → palette color. Same source always gets the same
 * color across loads and across the app. Palette is limited to 10 colors so
 * distinct sources are visually distinguishable.
 *
 * Palette keys map to tailwind color families; callers that need exact
 * classes use the helpers below (stripe, dot, faintBg) which ship the
 * complete class strings — needed because Tailwind purges any class it
 * doesn't see as a literal.
 */

const PALETTE = [
  "sky",
  "violet",
  "amber",
  "emerald",
  "rose",
  "orange",
  "cyan",
  "pink",
  "teal",
  "indigo",
] as const;

export type SourceColor = (typeof PALETTE)[number];

export function sourceColor(sourceId: string): SourceColor {
  // Simple djb2-style hash; stable, no deps, collision-tolerant for ~10 buckets.
  let h = 5381;
  for (let i = 0; i < sourceId.length; i += 1) {
    h = (h * 33) ^ sourceId.charCodeAt(i);
  }
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/** 3px colored left-border class for the card stripe. */
export function stripeClass(c: SourceColor): string {
  switch (c) {
    case "sky":
      return "border-l-sky-400/70";
    case "violet":
      return "border-l-violet-400/70";
    case "amber":
      return "border-l-amber-400/70";
    case "emerald":
      return "border-l-emerald-400/70";
    case "rose":
      return "border-l-rose-400/70";
    case "orange":
      return "border-l-orange-400/70";
    case "cyan":
      return "border-l-cyan-400/70";
    case "pink":
      return "border-l-pink-400/70";
    case "teal":
      return "border-l-teal-400/70";
    case "indigo":
      return "border-l-indigo-400/70";
  }
}

/** Colored dot next to the source name. */
export function dotClass(c: SourceColor): string {
  switch (c) {
    case "sky":
      return "bg-sky-400";
    case "violet":
      return "bg-violet-400";
    case "amber":
      return "bg-amber-400";
    case "emerald":
      return "bg-emerald-400";
    case "rose":
      return "bg-rose-400";
    case "orange":
      return "bg-orange-400";
    case "cyan":
      return "bg-cyan-400";
    case "pink":
      return "bg-pink-400";
    case "teal":
      return "bg-teal-400";
    case "indigo":
      return "bg-indigo-400";
  }
}
